import {
  type CreateGuide, type GuideApi, type GuideContext, type GuideCreateOptions, type GuideHost, type GuideSaveJson, type GuideScreen,
  type GuideScript, type GuideSignal, type GuideStep, type GuideTopicId,
} from "./guide";
import { GuideQueue, type QueueEvent } from "./guideQueue";
import { applyLegacyMirror, loadGuideSave } from "./guideSave";
import { createOverlay, type GuideView, type GuideViewHandlers } from "./guideOverlay";
import { surfaceClear, targetOnScreen } from "./guideDom";
import { playLines, type GuideLine } from "./guideWidgets";
import { topicLines as designedTopicLines } from "./topics";
import { bramDebug, bramSnap, preloadBram } from "./bram/bramView";

/**
 * Captain Bram's guide at run time (DESIGN §4): the one GuideApi the screens talk to. It keeps the hosts the screens
 * attach, feeds their facts to the pure GuideQueue, and turns the queue's events into the overlay, the level's sim hold
 * and the save. While a step is up or waiting it re-checks on a short pump (the tip clock, the grace after a banner),
 * so screens only need to call update() when their facts change.
 */

/** A no-op guide: every screen behaves as if there were none (kept for callers that want an explicit stand-in). */
export const NOOP_GUIDE: GuideApi = {
  register() {}, attach() {}, detach() {}, update() {}, signal() {},
  active: null, holdsSim: false, tipsOff: false,
  quiet: () => true, isDone: () => false, skipWalkthrough() {}, setTipsOff() {},
  replayTopic(_t, _h, done) { done(); }, startPractice() {},
};

/**
 * A first session starts fetching the captain's model this long after boot (the welcome card is his first line), so
 * the hub's own loading goes first. A returning player loads him when he next speaks.
 */
const BRAM_PRELOAD_DELAY_MS = 1500;

/** How often the controller re-checks while a step is up or queued. */
const PUMP_MS = 100;
/** A topic replay plays at most this many lines (DESIGN §6.6: 1–3). */
const TOPIC_LINES = 3;
const NO_SIGNALS: ReadonlySet<GuideSignal> = new Set();
/** The debug timeline keeps this many entries (a level's worth of lines and busy changes). */
const TIMELINE_CAP = 600;

/**
 * One entry of the guide's debug timeline (`window.__td.guide.timeline`, ?debug): a step shown, ended, put aside or
 * back, or the host's busy reason changing. The guide smoke reads it to fail on a line that blinks.
 */
export interface GuideTimelineEntry {
  readonly t: number;
  readonly type: "show" | "end" | "aside" | "back" | "busy";
  readonly id: string | null;
  readonly reason?: string;
}

/** What the controller needs from the page; tests pass fakes. */
export interface GuideDeps {
  /** The overlay, or null to run headless. */
  readonly view: (h: GuideViewHandlers) => GuideView | null;
  readonly schedule: (fn: () => void, ms: number) => unknown;
  /** The page is visible (tip clocks stop while it is not). */
  readonly visible: () => boolean;
  /** Plays topic lines inside the Guide sheet. */
  readonly play: (host: HTMLElement, lines: readonly GuideLine[], done: () => void) => void;
  /** Install `window.__td.guide` (?debug). */
  readonly debug: boolean;
  /** Whether a `needsTarget` step's target is on screen (guideDom.ts targetOnScreen); absent: always. */
  readonly targetReady?: (step: GuideStep, host: GuideHost) => boolean;
  /** M45: nothing covers the step's target or its screen (guideDom.ts surfaceClear); absent: always clear. */
  readonly surfaceClear?: (step: GuideStep, host: GuideHost) => boolean;
}

/** `window.__td.guide` in ?debug builds: for captures and the debug menu. */
export interface GuideDebugHook {
  readonly active: string | null;
  readonly done: readonly string[];
  /** Show a registered step now, whatever its conditions (it does not mark it done). */
  show(id: string): boolean;
  reset(): void;
  /** The captain's renderer: live or still, frames drawn, which model. */
  bram(): ReturnType<typeof bramDebug>;
  /** The next frame of the live captain as a PNG data URL (frame strips). */
  bramSnap(ms?: number): Promise<string | null>;
  /** Every show / end / aside / back and busy change, oldest first (at most TIMELINE_CAP). */
  readonly timeline: readonly GuideTimelineEntry[];
  /** M45 captures: a registered step's kind, screen, target and words (null if no such step). */
  step(id: string): { kind: string; screen: GuideScreen; target: GuideStep["target"] | null; text: string } | null;
  /** Every registered step id. */
  readonly ids: readonly string[];
}

export class GuideController implements GuideApi {
  private readonly queue: GuideQueue;
  private readonly hosts = new Map<GuideScreen, GuideHost>();
  /** Attached screens, the newest last: the current screen is the newest still in the document. */
  private stack: GuideScreen[] = [];
  private readonly ctxs = new Map<GuideScreen, GuideContext>();
  private readonly signals = new Set<GuideSignal>();
  private holdHost: GuideHost | null = null;
  private view: GuideView | null = null;
  private pump: unknown = null;
  private readonly handlers: GuideViewHandlers;
  private readonly timeline: GuideTimelineEntry[] = [];
  private lastBusy: string | null = null;

  constructor(private readonly o: GuideCreateOptions, private readonly deps: GuideDeps) {
    const { save, migrated } = loadGuideSave(o.saved, o.legacy, o.unlocks);
    this.queue = new GuideQueue(save, o.now, (s) => this.persist(s));
    if (migrated) this.persist(this.queue.save);
    this.handlers = {
      tap: () => this.act(this.queue.tap()),
      cta: (alt) => this.act(alt ? [...this.queue.skipWalkthrough(), ...this.queue.finish("alt")] : this.queue.finish("cta")),
      close: () => this.act(this.queue.finish("close")),
      skip: () => {
        const step = this.queue.active;
        if (!step) return;
        this.act(this.queue.isWalkthrough(step) ? this.queue.skipWalkthrough() : this.queue.setTipsOff(true));
      },
    };
    if (deps.debug) installDebugHook(this.debugHook());
  }

  private persist(save: GuideSaveJson): void {
    applyLegacyMirror(this.o.legacy, save);
    this.o.persist(save);
  }

  // ---------------------------------------------------------------------------------------------- GuideApi

  register(script: GuideScript): void { this.queue.register(script); }

  attach(screen: GuideScreen, host: GuideHost): void {
    const before = this.current;
    const prev = this.hosts.get(screen);
    this.hosts.set(screen, host);
    this.stack = [...this.stack.filter((s) => s !== screen), screen];
    // A step up on another screen gives way to the one that just mounted.
    const a = this.queue.active;
    if (a && a.screen !== screen) this.apply(this.queue.withdraw(a.screen));
    // A screen re-attaching (the hub re-renders) keeps its step; a new root gets the overlay moved onto it.
    else if (a && prev && prev.root !== host.root) this.overlay()?.show(a, host, this.skipKind(a));
    if (before !== screen || prev?.root !== host.root) this.queue.screenChanged();
    this.run();
  }

  detach(screen: GuideScreen): void {
    if (!this.hosts.has(screen)) return;
    // Signals raised on the way out (PLAY pressed) still count for the step they finish.
    const ctx = this.ctxs.get(screen);
    if (ctx && this.signals.size > 0 && this.current === screen) this.update(ctx);
    this.apply(this.queue.withdraw(screen));
    this.hosts.delete(screen);
    this.ctxs.delete(screen);
    this.stack = this.stack.filter((s) => s !== screen);
    this.queue.screenChanged();
    this.run();
  }

  update(ctx: GuideContext): void {
    const signals = this.signals.size > 0 ? new Set([...ctx.signals, ...this.signals]) : ctx.signals;
    this.signals.clear();
    if (ctx.screen !== this.current) { this.ctxs.set(ctx.screen, { ...ctx, signals: NO_SIGNALS }); return; }
    this.ctxs.set(ctx.screen, { ...ctx, signals });
    this.run();
    // A signal is seen by one evaluation only; the pump re-checks with the facts alone.
    this.ctxs.set(ctx.screen, { ...ctx, signals: NO_SIGNALS });
  }

  signal(s: GuideSignal): void { this.signals.add(s); }

  get active(): string | null { return this.queue.active?.id ?? null; }
  get holdsSim(): boolean { return this.holdHost !== null; }
  quiet(): boolean { return this.queue.quiet(); }
  isDone(id: string): boolean { return this.queue.isDone(id); }
  skipWalkthrough(): void { this.act(this.queue.skipWalkthrough()); }
  get tipsOff(): boolean { return this.queue.tipsAreOff; }
  get walkthroughSkipped(): boolean { return this.queue.skipped; }
  setTipsOff(off: boolean): void { this.act(this.queue.setTipsOff(off)); }
  startPractice(): void { this.queue.startPractice(); }

  /** The topic's own lines (its steps' words, info and tips first), played as info cards; no targets in the sheet. */
  replayTopic(topic: GuideTopicId, host: HTMLElement, done: () => void): void {
    // the Guide sheet's written lines for the topic (topics.ts) first; the steps' own lines when a topic has none
    const designed = designedTopicLines(topic).slice(0, TOPIC_LINES).map((text): GuideLine => ({ text, pose: "neutral" }));
    this.deps.play(host, designed.length > 0 ? designed : this.topicLines(topic), done);
  }

  topicLines(topic: GuideTopicId): GuideLine[] {
    const ctx = this.ctxs.get(this.current ?? "hub") ?? null;
    const rank = (s: GuideStep) => (s.kind === "info" ? 0 : s.kind === "tip" ? 1 : 2);
    const steps = this.queue.registered.filter((s) => s.topic === topic && s.kind !== "welcome").sort((a, b) => rank(a) - rank(b));
    const out: GuideLine[] = [];
    for (const s of steps) {
      const text = this.queue.text(s, ctx);
      if (text && !out.some((l) => l.text === text)) out.push({ text, pose: s.pose });
      if (out.length >= TOPIC_LINES) break;
    }
    return out;
  }

  // ---------------------------------------------------------------------------------------------- running

  /** The newest attached screen whose root is still in the document. */
  private get current(): GuideScreen | null {
    for (let i = this.stack.length - 1; i >= 0; i--) {
      const host = this.hosts.get(this.stack[i]);
      if (host && (host.root as { isConnected?: boolean }).isConnected !== false) return this.stack[i];
    }
    return null;
  }

  private busy(host: GuideHost | undefined): ReturnType<GuideHost["busy"]> {
    if (!host) return null;
    try { return host.busy(); } catch { return null; }
  }

  private run(): void {
    const screen = this.current;
    const host = screen ? this.hosts.get(screen) : undefined;
    const ctx = screen ? this.ctxs.get(screen) ?? null : null;
    const probe = this.deps.targetReady;
    const ready = probe && host ? (step: GuideStep) => probe(step, host) : undefined;
    const surface = this.deps.surfaceClear;
    const clear = surface ? (step: GuideStep) => { const h = this.hosts.get(step.screen); return !h || surface(step, h); } : undefined;
    const busy = this.busy(host);
    if (busy !== this.lastBusy) { this.lastBusy = busy; this.note({ t: this.o.now(), type: "busy", id: null, reason: busy ?? "quiet" }); }
    this.apply(this.queue.evaluate(ctx, { busy, visible: this.deps.visible(), ready, clear }));
    this.draw();
    this.schedule();
  }

  private act(events: QueueEvent[]): void { this.apply(events); this.run(); }

  private schedule(): void {
    if (this.pump !== null || (this.queue.active === null && this.queue.pending === 0)) return;
    this.pump = this.deps.schedule(() => { this.pump = null; this.run(); }, PUMP_MS);
  }

  private overlay(): GuideView | null {
    if (!this.view) this.view = this.deps.view(this.handlers);
    return this.view;
  }

  private note(e: GuideTimelineEntry): void {
    if (this.timeline.length >= TIMELINE_CAP) this.timeline.shift();
    this.timeline.push(e);
  }

  private apply(events: readonly QueueEvent[]): void {
    for (const e of events) {
      this.note({ t: this.o.now(), type: e.type, id: e.step.id, ...(e.type === "end" ? { reason: `${e.reason}${e.done ? "" : " (not done)"}` } : {}) });
      if (e.type === "show") this.onShow(e.step);
      else if (e.type === "end") this.onEnd(e.step, e.done);
    }
  }

  private onShow(step: GuideStep): void {
    const host = this.hosts.get(step.screen);
    if (!host) return;
    if (import.meta.env.DEV) console.debug(`[guide] shown ${step.id}`);
    if (step.holdSim && host.hold && this.holdHost === null) { this.holdHost = host; host.hold(true); }
    const t = step.target;
    if (t && "world" in t && t.focus) { try { host.world?.focus(t.world); } catch { /* a camera hiccup must not stop the line */ } }
    this.overlay()?.show(step, host, this.skipKind(step));
  }

  /**
   * Walkthrough bubbles offer "Skip tips" (skips the walkthrough); in-battle hints offer it too (tips off). A warning
   * does not: tips off would not silence it (GuideQueue.isSafety), so the chip would promise what it cannot do.
   */
  private skipKind(step: GuideStep): false | "walkthrough" | "hints" {
    if (step.kind === "welcome" || step.kind === "tip" || this.queue.isSafety(step)) return false;
    if (this.queue.isWalkthrough(step)) return "walkthrough";
    return step.screen === "level" ? "hints" : false;
  }

  private onEnd(step: GuideStep, done: boolean): void {
    if (import.meta.env.DEV) console.debug(`[guide] ${done ? "done" : "skip"} ${step.id}`);
    this.releaseHold();
    this.view?.clear();
  }

  private releaseHold(): void {
    const h = this.holdHost;
    if (!h) return;
    this.holdHost = null;
    h.hold?.(false);
  }

  private draw(): void {
    const v = this.queue.view();
    if (v) this.view?.refresh(v);
  }

  private debugHook(): GuideDebugHook {
    const self = this;
    return {
      get active() { return self.active; },
      get done() { return self.queue.doneIds; },
      show(id: string) { const ok = self.queue.forceShow(id); if (ok) self.run(); return ok; },
      reset() { self.act(self.queue.reset()); },
      bram: bramDebug,
      bramSnap,
      get timeline() { return self.timeline; },
      step(id: string) {
        const s = self.queue.step(id);
        return s ? { kind: s.kind, screen: s.screen, target: s.target ?? null, text: self.queue.text(s, self.ctxs.get(s.screen) ?? null) } : null;
      },
      get ids() { return self.queue.registered.map((s) => s.id); },
    };
  }
}

/** main.ts replaces `window.__td` at the end of its module; re-attach after that assignment. */
function installDebugHook(hook: GuideDebugHook): void {
  const put = () => { const w = window as unknown as { __td?: Record<string, unknown> }; (w.__td ??= {}).guide = hook; };
  put();
  window.setTimeout(put, 0);
}

function browserDeps(): GuideDeps {
  const env = (import.meta as { env?: { DEV?: boolean } }).env;
  return {
    view: (h) => createOverlay(h),
    schedule: (fn, ms) => window.setTimeout(fn, ms),
    visible: () => document.visibilityState !== "hidden",
    play: (host, lines, done) => { playLines(host, lines, done); },
    debug: env?.DEV === true || new URLSearchParams(location.search).has("debug"),
    targetReady: targetOnScreen,
    surfaceClear,
  };
}

export const createGuide: CreateGuide = (o) => {
  const g = new GuideController(o, browserDeps());
  if (o.legacy.highestLevelCleared === 0 && (o.saved?.done.length ?? 0) === 0) window.setTimeout(preloadBram, BRAM_PRELOAD_DELAY_MS);
  return g;
};
